import React, { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Plus, Minus, ShoppingCart, User, MapPin, Utensils, Clock, X, Edit, WifiOff } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useOfflineCategories } from '@/hooks/useOfflineCategories'
import { useOfflineMenuItems } from '@/hooks/useOfflineMenuItems'
import { useDeliveryZones } from '@/hooks/useDeliveryZones'
import { useNetwork } from '@/contexts/NetworkContext'
import { useAuth } from '@/contexts/AuthContext'
import { updateOrderItem, deleteOrderItem, createOrderItem, restoreOrderItem, batchUpdateOrderItems, batchAllOrderOperations } from '@/services/orderService'
import { OrderType, MenuItem, DeliveryZone, Order, OrderItem as OrderItemType } from '@/types/index'
import { supabase } from '@/integrations/supabase/client'
import type { Database } from '@/integrations/supabase/types'

interface OrderItem {
  menuItem: MenuItem
  variant?: { id: string; size_name: string; price: number }
  quantity: number
  notes?: string
  originalItemId?: string // To track existing items
  originalUnitPrice?: number // To preserve the original price from database
  isRemoved?: boolean // Track if item is marked for removal
  wasRemoved?: boolean // Track if item was previously removed
}

interface EditOrderDialogProps {
  order: Order
  currency?: string
  onOrderUpdated: () => void
  children: React.ReactNode
  open: boolean
  setOpen: (open: boolean) => void
}

interface CustomerInfo {
  name: string
  phone: string
  email: string
  address: string
  tableNumber: string
  notes: string
}

export const EditOrderDialog: React.FC<EditOrderDialogProps> = ({
  order,
  currency = 'USD',
  onOrderUpdated,
  children,
  open,
  setOpen
}) => {
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: order.customer_name || '',
    phone: order.customer_phone || '',
    email: order.customer_email || '',
    address: order.customer_address || '',
    tableNumber: order.table_number || '',
    notes: order.notes || ''
  })
  const [orderType, setOrderType] = useState<OrderType>(order.order_type)
  const [selectedDeliveryZone, setSelectedDeliveryZone] = useState<DeliveryZone | null>(null)
  const [orderItems, setOrderItems] = useState<OrderItem[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  
  const { toast } = useToast()
  const { isOnline, isElectronApp } = useNetwork()
  const { isStaff } = useAuth()
  const { categories, loading: categoriesLoading, isOffline: categoriesOffline } = useOfflineCategories(order.branch_id)
  const { menuItems, loading: menuItemsLoading, isOffline: menuItemsOffline } = useOfflineMenuItems(selectedCategory, order.branch_id)
  const { deliveryZones, loading: zonesLoading } = useDeliveryZones(order.branch_id)

  // Allow editing unless status is 'completed' or 'cancelled'
  const canEditOrder = order.status !== 'completed' && order.status !== 'cancelled'

  useEffect(() => {
    if (open && order) {
      setIsLoading(true)
      setCustomerInfo({
        name: order.customer_name || '',
        phone: order.customer_phone || '',
        email: order.customer_email || '',
        address: order.customer_address || '',
        tableNumber: order.table_number || '',
        notes: order.notes || ''
      })
      setOrderType(order.order_type)
      const convertedItems: OrderItem[] = order.order_items.map(item => {
        const menuItem = item.menu_items!;
        const variants = menuItem.variants;
        let variant = undefined;
        
        if (variants && Array.isArray(variants) && variants.length > 0 && item.unit_price !== undefined) {
          variant = variants.find(v => v.price === item.unit_price);
        }
        
        return {
          menuItem,
          variant,
          quantity: item.quantity,
          notes: item.notes || '',
          originalItemId: item.id,
          originalUnitPrice: item.unit_price,
          isRemoved: item.is_removed || false,
          wasRemoved: item.is_removed || false
        };
      })
      setOrderItems(convertedItems)
      setIsLoading(false)
    }
  }, [open, order.id, deliveryZones])

  useEffect(() => {
    if (Array.isArray(categories) && categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0].id)
    }
  }, [categories, selectedCategory])

  // Handle delivery zone updates separately to avoid resetting the entire dialog
  useEffect(() => {
    if (open && order.delivery_zone_id && Array.isArray(deliveryZones) && deliveryZones.length > 0 && !selectedDeliveryZone) {
      const zone = deliveryZones.find(z => z.id === order.delivery_zone_id)
      setSelectedDeliveryZone(zone || null)
    }
  }, [open, order.delivery_zone_id, deliveryZones, selectedDeliveryZone])

  const subtotal = orderItems.reduce((sum, item) => {
    // Skip removed items in total calculation
    if (item.isRemoved) return sum
    
    // Use original unit price if available (for existing items), otherwise use variant or base price
    const price = item.originalUnitPrice !== undefined && !isNaN(item.originalUnitPrice)
      ? item.originalUnitPrice
      : (typeof item.variant?.price === 'number' && !isNaN(item.variant?.price)
        ? item.variant.price
        : (typeof item.menuItem.base_price === 'number' && !isNaN(item.menuItem.base_price) ? item.menuItem.base_price : 0))
    return sum + (price * item.quantity)
  }, 0)

  const deliveryFee = orderType === 'delivery' && selectedDeliveryZone ? selectedDeliveryZone.delivery_fee : 0
  const total = subtotal + deliveryFee

  const isFormValid = useMemo(() => {
    const hasItems = orderItems.length > 0
    const hasTableNumber = orderType !== 'dine_in' || customerInfo.tableNumber.trim()
    const hasDeliveryInfo = orderType !== 'delivery' || (selectedDeliveryZone && customerInfo.address.trim())
    const meetsMinimumOrder = orderType !== 'delivery' || !selectedDeliveryZone || subtotal >= selectedDeliveryZone.minimum_order_amount
    return hasItems && hasTableNumber && hasDeliveryInfo && meetsMinimumOrder
  }, [customerInfo, orderItems, orderType, selectedDeliveryZone, subtotal])

  const matchesMenuSelection = (item: OrderItem, menuItem: MenuItem, variant?: { id: string; size_name: string; price: number }) =>
    item.menuItem.id === menuItem.id &&
    item.variant?.id === variant?.id

  const addItemToOrder = (menuItem: MenuItem, variant?: { id: string; size_name: string; price: number }) => {
    const existingItemIndex = orderItems.findIndex(item =>
      matchesMenuSelection(item, menuItem, variant) && !item.isRemoved
    )
    
    if (existingItemIndex >= 0) {
      const updatedItems = [...orderItems]
      updatedItems[existingItemIndex].quantity += 1
      setOrderItems(updatedItems)
      return
    }

    const removedItemIndex = orderItems.findIndex(item =>
      matchesMenuSelection(item, menuItem, variant) && item.isRemoved
    )

    if (removedItemIndex >= 0) {
      setOrderItems(prev => prev.map((item, i) =>
        i === removedItemIndex ? { ...item, isRemoved: false } : item
      ))
    } else {
      setOrderItems(prev => [...prev, {
        menuItem,
        variant,
        quantity: 1,
        notes: ''
      }])
    }
  }

  const updateItemQuantity = (index: number, quantity: number) => {
    if (quantity <= 0) {
      setOrderItems(prev => prev.flatMap((item, i) => {
        if (i !== index) return [item]
        return item.originalItemId ? [{ ...item, isRemoved: true }] : []
      }))
    } else {
      setOrderItems(prev => prev.map((item, i) => 
        i === index ? { ...item, quantity } : item
      ))
    }
  }

  const updateItemNotes = (index: number, notes: string) => {
    setOrderItems(prev => prev.map((item, i) => 
      i === index ? { ...item, notes } : item
    ))
  }

  const removeItem = (index: number) => {
    setOrderItems(prev => prev.map((item, i) => 
      i === index ? { ...item, isRemoved: true } : item
    ))
  }

  const restoreItem = (index: number) => {
    setOrderItems(prev => prev.map((item, i) => 
      i === index ? { ...item, isRemoved: false } : item
    ))
  }

  const removeItemFromOrder = (menuItem: MenuItem, variant?: { id: string; size_name: string; price: number }) => {
    setOrderItems(prev => prev.flatMap(item => {
      if (!matchesMenuSelection(item, menuItem, variant)) return [item]
      return item.originalItemId ? [{ ...item, isRemoved: true }] : []
    }))
  }

  const isItemInOrder = (menuItem: MenuItem, variant?: { id: string; size_name: string; price: number }) => {
    return orderItems.some(item =>
      matchesMenuSelection(item, menuItem, variant) &&
      !item.isRemoved
    )
  }

  const handleUpdateOrder = async () => {
    if (!isFormValid || !canEditOrder) {
      toast({
        title: 'Cannot Edit Order',
        description: order.status === 'completed' ? 'Completed orders cannot be edited.' : 'Cancelled orders cannot be edited.',
        variant: 'destructive',
      })
      return
    }
    setIsSubmitting(true)
    
    // Show progress toast
    toast({
      title: 'Updating Order...',
      description: 'Please wait while we save your changes.',
    })
    
    try {
      // If this is a session order, update all orders in the session
      const ordersToUpdate = order.isSessionOrder && order.sessionOrders && Array.isArray(order.sessionOrders) ? order.sessionOrders : [order]
      
      // Batch update all orders
      const orderUpdates = ordersToUpdate.map(orderToUpdate => 
        supabase
          .from('orders')
          .update({
            customer_name: customerInfo.name.trim() || 'Walk-in Customer',
            customer_phone: customerInfo.phone.trim() || '',
            customer_email: customerInfo.email || null,
            customer_address: customerInfo.address || null,
            notes: customerInfo.notes || null,
            order_type: orderType,
            table_number: orderType === 'dine_in' ? customerInfo.tableNumber : null,
            delivery_zone_id: orderType === 'delivery' ? selectedDeliveryZone?.id : null,
            delivery_fee: deliveryFee,
            updated_at: new Date().toISOString()
          } as Database['public']['Tables']['orders']['Update'])
          .eq('id', orderToUpdate.id)
      )
      
      const orderResults = await Promise.all(orderUpdates)
      for (const result of orderResults) {
        if (result.error) {
          console.error('Error updating order:', result.error)
          throw new Error(`Failed to update order: ${result.error.message}`)
        }
      }

      // Handle order items with SUPER BATCH for maximum speed
      console.log('🚀 Preparing super batch operations...')
      
      // Prepare all operations for the super batch
      const updates = orderItems
        .filter(item => item.originalItemId && !item.isRemoved)
        .map(item => ({
          orderItemId: item.originalItemId!,
          updates: {
            quantity: item.quantity,
            notes: item.notes || undefined
          }
        }))

      const creates = orderItems
        .filter(item => !item.originalItemId && !item.isRemoved)
        .map(item => {
          const unitPrice = typeof item.variant?.price === 'number' && !isNaN(item.variant?.price) 
            ? item.variant.price 
            : (typeof item.menuItem.base_price === 'number' && !isNaN(item.menuItem.base_price) ? item.menuItem.base_price : 0)
          
          return {
            orderId: order.id,
            menuItemId: item.menuItem.id,
            quantity: item.quantity,
            unitPrice,
            notes: item.notes,
            variantId: item.variant?.id
          }
        })

      const removes = orderItems
        .filter(item => item.isRemoved && item.originalItemId)
        .map(item => ({
          orderItemId: item.originalItemId!
        }))

      const restores = orderItems
        .filter(item => !item.isRemoved && item.originalItemId && item.wasRemoved)
        .map(item => ({
          orderItemId: item.originalItemId!
        }))

      // Execute ALL operations in one super-batch call
      console.log(`🔥 Super batch: ${updates.length} updates, ${creates.length} creates, ${removes.length} removes, ${restores.length} restores`)
      
      const superBatchResult = await batchAllOrderOperations({
        updates: updates.length > 0 ? updates : undefined,
        creates: creates.length > 0 ? creates : undefined,
        removes: removes.length > 0 ? removes : undefined,
        restores: restores.length > 0 ? restores : undefined
      }, isStaff)

      if (!superBatchResult.success) {
        throw new Error(`Failed to execute super batch operations: ${superBatchResult.error}`)
      }

      // Update total amounts for all orders in the session
      if (order.isSessionOrder && order.sessionOrders && Array.isArray(order.sessionOrders)) {
        for (const orderToUpdate of order.sessionOrders) {
          const orderItems = await supabase
            .from('order_items')
            .select('quantity, unit_price')
            .eq('order_id', orderToUpdate.id)
            .eq('is_removed', false)
          
          if (orderItems.data) {
            const orderTotal = orderItems.data.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0)
            await supabase
              .from('orders')
              .update({ total_amount: orderTotal })
              .eq('id', orderToUpdate.id)
          }
        }
      } else {
        // Update total for single order
        await supabase
          .from('orders')
          .update({ total_amount: total })
          .eq('id', order.id)
      }

      toast({
        title: 'Order Updated Successfully!',
        description: order.isSessionOrder ? `Session orders have been updated` : `Order #${order.order_number} has been updated`,
      })
      setOpen(false)
      onOrderUpdated()
    } catch (error) {
      console.error('Error updating order:', error)
      toast({
        title: 'Error Updating Order',
        description: error instanceof Error ? error.message : 'Failed to update order. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatPrice = (price: number | undefined | null) => {
    const safePrice = typeof price === 'number' && !isNaN(price) ? price : 0
    return `${safePrice.toFixed(2)} ${currency}`
  }

  if (!canEditOrder) {
    return (
      <Button variant="outline" size="sm" disabled>
        <Edit className="h-3 w-3 mr-1" />
        Cannot Edit
      </Button>
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Edit Order #{order.order_number}</DialogTitle>
              <DialogDescription>
                Modify order items, customer information, and order details
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge 
                variant={order.status === 'completed' ? 'secondary' : order.status === 'cancelled' ? 'destructive' : 'default'}
                className="text-xs"
              >
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </Badge>
              {!canEditOrder && (
                <Badge variant="destructive" className="text-xs">
                  Read Only
                </Badge>
              )}
            </div>
          </div>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading order data...</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden">
            <Tabs defaultValue="customer" className="h-full flex flex-col">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="customer" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Customer Info
                </TabsTrigger>
                <TabsTrigger value="menu" className="flex items-center gap-2">
                  <Utensils className="h-4 w-4" />
                  Menu Selection
                </TabsTrigger>
                <TabsTrigger value="review" className="flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4" />
                  Review & Update
                  {orderItems.length > 0 && (
                    <Badge variant="secondary" className="ml-1">
                      {orderItems.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              <div className="flex-1 overflow-hidden">
                <TabsContent value="customer" className="h-[500px] mt-4">
                  <ScrollArea className="h-full pr-4">
                    <div className="space-y-6 pb-20">
                      {/* Order Type Selection */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Order Type</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <RadioGroup value={orderType} onValueChange={(value) => setOrderType(value as OrderType)}>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="pickup" id="pickup" />
                              <Label htmlFor="pickup" className="flex items-center space-x-2 cursor-pointer">
                                <Utensils className="h-4 w-4" />
                                <span>Pickup</span>
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="delivery" id="delivery" />
                              <Label htmlFor="delivery" className="flex items-center space-x-2 cursor-pointer">
                                <MapPin className="h-4 w-4" />
                                <span>Delivery</span>
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="dine_in" id="dine_in" />
                              <Label htmlFor="dine_in" className="flex items-center space-x-2 cursor-pointer">
                                <Clock className="h-4 w-4" />
                                <span>Dine In</span>
                              </Label>
                            </div>
                          </RadioGroup>
                        </CardContent>
                      </Card>
                      {/* Customer Information */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Customer Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="customer-name">Name</Label>
                              <Input
                                id="customer-name"
                                value={customerInfo.name}
                                onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Customer's full name (optional)"
                                disabled={!canEditOrder}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="customer-phone">Phone</Label>
                              <Input
                                id="customer-phone"
                                value={customerInfo.phone}
                                onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                                placeholder="Customer's phone number (optional)"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="customer-email">Email</Label>
                            <Input
                              id="customer-email"
                              type="email"
                              value={customerInfo.email}
                              onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                              placeholder="Customer's email address"
                            />
                          </div>
                          {/* Conditional fields based on order type */}
                          {orderType === 'delivery' && (
                            <>
                              <div className="space-y-2">
                                <Label htmlFor="customer-address">Delivery Address *</Label>
                                <Input
                                  id="customer-address"
                                  value={customerInfo.address}
                                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, address: e.target.value }))}
                                  placeholder="Customer's delivery address"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="delivery-zone">Delivery Zone *</Label>
                                {zonesLoading ? (
                                  <div className="text-sm text-gray-500">Loading delivery zones...</div>
                                ) : Array.isArray(deliveryZones) && deliveryZones.length > 0 ? (
                                  <Select value={selectedDeliveryZone?.id || ''} onValueChange={(value) => {
                                    const zone = deliveryZones.find(z => z.id === value)
                                    setSelectedDeliveryZone(zone || null)
                                  }}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select delivery zone" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {deliveryZones.map(zone => (
                                        <SelectItem key={zone.id} value={zone.id}>
                                          <div className="flex justify-between items-center w-full">
                                            <span>{zone.zone_name}</span>
                                            <span className="text-sm text-gray-500 ml-4">
                                              Fee: {formatPrice(zone.delivery_fee)} · Min: {formatPrice(zone.minimum_order_amount)}
                                            </span>
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <div className="text-sm text-red-500">No delivery zones available</div>
                                )}
                              </div>
                            </>
                          )}
                          {orderType === 'dine_in' && (
                            <div className="space-y-2">
                              <Label htmlFor="table-number">Table Number *</Label>
                              <Input
                                id="table-number"
                                value={customerInfo.tableNumber}
                                onChange={(e) => setCustomerInfo(prev => ({ ...prev, tableNumber: e.target.value }))}
                                placeholder="Table number"
                              />
                            </div>
                          )}
                          <div className="space-y-2">
                            <Label htmlFor="order-notes">Special Notes</Label>
                            <Textarea
                              id="order-notes"
                              value={customerInfo.notes}
                              onChange={(e) => setCustomerInfo(prev => ({ ...prev, notes: e.target.value }))}
                              placeholder="Any special requests or notes"
                              rows={3}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </ScrollArea>
                </TabsContent>
                <TabsContent value="menu" className="h-[500px] mt-4">
                  <div className="flex flex-col gap-4">
                    <div className="h-[400px] flex gap-4">
                      <div className="w-1/4 min-w-[200px]">
                        <Card className="h-full">
                          <CardHeader>
                            <CardTitle className="text-lg">Categories</CardTitle>
                          </CardHeader>
                          <CardContent className="p-0">
                            <ScrollArea className="h-[320px]">
                              {categoriesLoading ? (
                                <div className="p-4 text-sm text-gray-500">Loading categories...</div>
                              ) : (
                                <div className="space-y-1 p-4 pb-20">
                                  {categories.map((category) => (
                                    <Button
                                      key={category.id}
                                      variant={selectedCategory === category.id ? 'default' : 'ghost'}
                                      className="w-full justify-start text-left"
                                      onClick={() => setSelectedCategory(category.id)}
                                    >
                                      {category.name}
                                    </Button>
                                  ))}
                                </div>
                              )}
                            </ScrollArea>
                          </CardContent>
                        </Card>
                      </div>
                      <div className="flex-1">
                        <Card className="h-full">
                          <CardHeader className="pb-2">
                            <div className="flex justify-between items-center">
                              <CardTitle className="text-lg">Menu Items</CardTitle>
                              {orderItems.length > 0 && (
                                <div className="bg-green-100 px-3 py-1 rounded-lg border border-green-300">
                                  <span className="text-sm font-semibold text-green-800">
                                    Total: {formatPrice(total)} ({orderItems.length} items)
                                  </span>
                                </div>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="p-0">
                            <ScrollArea className="h-[320px]">
                              {menuItemsLoading ? (
                                <div className="p-4 text-sm text-gray-500">Loading menu items...</div>
                              ) : menuItems.length === 0 ? (
                                <div className="p-4 text-sm text-gray-500">No items in this category</div>
                              ) : (
                                <div className="space-y-2 p-4 pb-20">
                                  {menuItems.map((item) => {
                                    const totalQuantity = orderItems
                                      .filter(orderItem => orderItem.menuItem.id === item.id && !orderItem.isRemoved)
                                      .reduce((sum, orderItem) => sum + orderItem.quantity, 0)
                                    return (
                                      <Card key={item.id} className={`border ${totalQuantity > 0 ? 'bg-blue-50 border-blue-200' : ''}`}>
                                        <CardContent className="p-4">
                                          <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2">
                                                <h4 className="font-medium">{item.name}</h4>
                                                {totalQuantity > 0 && (
                                                  <Badge variant="secondary" className="text-xs">
                                                    {totalQuantity} in order
                                                  </Badge>
                                                )}
                                              </div>
                                              {item.description && (
                                                <div className="text-sm text-gray-600 mt-1">{item.description}</div>
                                              )}
                                              {item.variants && item.variants.length > 0 ? (
                                                <div className="mt-3 space-y-2">
                                                  {item.variants.map((variant) => {
                                                    const variantQuantity = orderItems
                                                      .filter(orderItem => 
                                                        orderItem.menuItem.id === item.id && 
                                                        orderItem.variant?.id === variant.id &&
                                                        !orderItem.isRemoved
                                                      )
                                                      .reduce((sum, orderItem) => sum + orderItem.quantity, 0)
                                                    return (
                                                      <div key={variant.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                                        <div>
                                                          <span className="font-medium">{variant.size_name}</span>
                                                          <span className="text-sm text-gray-600 ml-2">{formatPrice(variant.price)}</span>
                                                          {variantQuantity > 0 && (
                                                            <Badge variant="outline" className="ml-2 text-xs">
                                                              {variantQuantity} in order
                                                            </Badge>
                                                          )}
                                                        </div>
                                                        <Button
                                                          size="sm"
                                                          variant={isItemInOrder(item, variant) ? 'destructive' : 'default'}
                                                          disabled={!canEditOrder}
                                                          onClick={() => {
                                                            if (isItemInOrder(item, variant)) {
                                                              removeItemFromOrder(item, variant)
                                                            } else {
                                                              addItemToOrder(item, variant)
                                                            }
                                                          }}
                                                        >
                                                          {isItemInOrder(item, variant) ? (
                                                            <Minus className="h-3 w-3" />
                                                          ) : (
                                                            <Plus className="h-3 w-3" />
                                                          )}
                                                        </Button>
                                                      </div>
                                                    )
                                                  })}
                                                </div>
                                              ) :
                                                <div className="flex justify-between items-center">
                                                  <span className="font-medium">{formatPrice(item.base_price)}</span>
                                                  <Button
                                                    size="sm"
                                                    variant={isItemInOrder(item) ? 'destructive' : 'default'}
                                                    disabled={!canEditOrder}
                                                    onClick={() => {
                                                      if (isItemInOrder(item)) {
                                                        removeItemFromOrder(item)
                                                      } else {
                                                        addItemToOrder(item)
                                                      }
                                                    }}
                                                  >
                                                    {isItemInOrder(item) ? (
                                                      <Minus className="h-3 w-3" />
                                                    ) : (
                                                      <Plus className="h-3 w-3" />
                                                    )}
                                                  </Button>
                                                </div>
                                              }
                                            </div>
                                          </div>
                                        </CardContent>
                                      </Card>
                                    )
                                  })}
                                </div>
                              )}
                            </ScrollArea>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="review" className="h-[500px] mt-4">
                  <ScrollArea className="h-full pr-4">
                    <div className="space-y-6 pb-20">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Order Summary</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {orderItems.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                              <p>No items in order</p>
                              <p className="text-sm">Go to Menu Selection to add items</p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {orderItems.map((item, index) => (
                                <div 
                                  key={`${item.menuItem.id || item.originalItemId || index}-${item.variant?.id || 'base'}`} 
                                  className={`flex items-start space-x-3 p-3 border rounded-lg ${
                                    item.isRemoved ? 'bg-red-50 border-red-200 opacity-75' : ''
                                  }`}
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <h4 className={`font-medium ${item.isRemoved ? 'line-through text-red-600' : ''}`}>
                                        {item.menuItem.name}
                                      </h4>
                                      {item.isRemoved && (
                                        <Badge variant="destructive" className="text-xs">
                                          REMOVED
                                        </Badge>
                                      )}
                                    </div>
                                    {item.variant && (
                                      <p className={`text-sm ${item.isRemoved ? 'text-red-500' : 'text-gray-600'}`}>
                                        {item.variant.size_name}
                                      </p>
                                    )}
                                    <p className={`text-sm font-medium ${item.isRemoved ? 'text-red-500' : ''}`}>
                                      {formatPrice(item.originalUnitPrice !== undefined ? item.originalUnitPrice : (item.variant?.price || item.menuItem.base_price))} × {item.quantity}
                                    </p>
                                    <div className="mt-2">
                                      <Input
                                        placeholder="Special notes for this item"
                                        value={item.notes || ''}
                                        onChange={(e) => updateItemNotes(index, e.target.value)}
                                        className={`text-sm ${item.isRemoved ? 'bg-red-50 border-red-200' : ''}`}
                                        disabled={item.isRemoved}
                                      />
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    {!item.isRemoved ? (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          disabled={!canEditOrder}
                                          onClick={() => updateItemQuantity(index, item.quantity - 1)}
                                        >
                                          <Minus className="h-3 w-3" />
                                        </Button>
                                        <span className="w-8 text-center">{item.quantity}</span>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          disabled={!canEditOrder}
                                          onClick={() => updateItemQuantity(index, item.quantity + 1)}
                                        >
                                          <Plus className="h-3 w-3" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          onClick={() => removeItem(index)}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => restoreItem(index)}
                                        className="text-green-600 border-green-300 hover:bg-green-50"
                                      >
                                        <Plus className="h-3 w-3" />
                                        Restore
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Order Summary</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span>Subtotal:</span>
                              <span>{formatPrice(subtotal)}</span>
                            </div>
                            {deliveryFee > 0 && (
                              <div className="flex justify-between">
                                <span>Delivery Fee:</span>
                                <span>{formatPrice(deliveryFee)}</span>
                              </div>
                            )}
                            <div className="flex justify-between font-bold text-lg">
                              <span>Total:</span>
                              <span>{formatPrice(total)}</span>
                            </div>
                          </div>
                          {orderType === 'delivery' && selectedDeliveryZone && subtotal < selectedDeliveryZone.minimum_order_amount && (
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mt-4">
                              <p className="text-sm text-yellow-800">
                                Minimum order amount for {selectedDeliveryZone.zone_name} is {formatPrice(selectedDeliveryZone.minimum_order_amount)}.
                                Add {formatPrice(selectedDeliveryZone.minimum_order_amount - subtotal)} more to proceed.
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Customer Details</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm">
                            <div><strong>Name:</strong> {customerInfo.name.trim() || 'Walk-in Customer'}</div>
                            <div><strong>Phone:</strong> {customerInfo.phone.trim() || 'Not provided'}</div>
                            {customerInfo.email && <div><strong>Email:</strong> {customerInfo.email}</div>}
                            <div><strong>Order Type:</strong> {orderType.replace('_', ' ').toUpperCase()}</div>
                            {orderType === 'delivery' && (
                              <>
                                <div><strong>Address:</strong> {customerInfo.address || 'Not provided'}</div>
                                <div><strong>Delivery Zone:</strong> {selectedDeliveryZone?.zone_name || 'Not selected'}</div>
                              </>
                            )}
                            {orderType === 'dine_in' && (
                              <div><strong>Table Number:</strong> {customerInfo.tableNumber || 'Not provided'}</div>
                            )}
                            {customerInfo.notes && <div><strong>Notes:</strong> {customerInfo.notes}</div>}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </ScrollArea>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateOrder} 
            disabled={!isFormValid || isSubmitting || !canEditOrder}
            className="min-w-[120px]"
          >
            {isSubmitting ? 'Updating...' : !canEditOrder ? 'Cannot Edit' : 'Update Order'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
